import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = isLogin
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(isLogin ? "Willkommen zurück!" : "Konto erstellt!");
      const staff = ["admin", "gutachter", "support"].includes(res.user.role);
      nav(staff ? "/admin" : "/portal");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0A192F] text-white p-12 hero-grid">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl">
          <span className="grid place-items-center h-9 w-9 rounded-md bg-white/10 text-emerald-400"><ShieldCheck size={20} /></span>
          Gründungszuschuss<span className="text-emerald-400">48h</span>
        </Link>
        <div>
          <h2 className="text-3xl font-bold leading-tight">Ihre fachkundige Stellungnahme in 48 Stunden.</h2>
          <p className="mt-4 text-slate-300">Melden Sie sich an und verfolgen Sie den Fortschritt Ihrer Anfrage jederzeit im Portal.</p>
        </div>
        <p className="text-slate-500 text-sm">© {new Date().getFullYear()} www.gruendungszuschuss48h.com</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 border-border shadow-sm">
          <h1 className="text-2xl font-bold">{isLogin ? "Anmelden" : "Konto erstellen"}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isLogin ? "Melden Sie sich in Ihrem Kundenportal an." : "Erstellen Sie ein Konto, um zu starten."}
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="auth-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Max Mustermann" />
              </div>
            )}
            <div>
              <Label htmlFor="email">E-Mail</Label>
              <Input id="email" type="email" data-testid="auth-email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="name@beispiel.de" />
            </div>
            <div>
              <Label htmlFor="password">Passwort</Label>
              <Input id="password" type="password" data-testid="auth-password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" placeholder="••••••••" />
            </div>
            <Button type="submit" data-testid="auth-submit" disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
              {loading ? "Bitte warten…" : isLogin ? "Anmelden" : "Registrieren"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-muted-foreground">
            {isLogin ? "Noch kein Konto? " : "Bereits registriert? "}
            <Link to={isLogin ? "/register" : "/login"} data-testid="auth-switch" className="text-emerald-600 font-semibold hover:underline">
              {isLogin ? "Registrieren" : "Anmelden"}
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
